import { SKILL_AP_PER_CARD, SKILL_BUFF_BASE_AP } from "@/character/skills/constants";
import type { SkillDefinition } from "@/character/skills/types";
import type { HomeEconomy } from "@/home/economy";
import {
  emptyAllocation,
  STAT_IDS,
  type StatAllocation,
  type StatId,
} from "@/game/model/character-stats";

export function skillApCardsApplied(skill: SkillDefinition): number {
  const n = skill.skillApCards ?? 0;
  return Math.max(0, Math.floor(n));
}

export function skillBuffApBudgetForCards(rawCards: number | undefined): number {
  const cards = Math.max(0, Math.floor(rawCards ?? 0));
  return SKILL_BUFF_BASE_AP + cards * SKILL_AP_PER_CARD;
}

export function skillBuffApBudget(skill: SkillDefinition): number {
  return skillBuffApBudgetForCards(skill.skillApCards);
}

export function buffAllocationFromSkill(skill: SkillDefinition): StatAllocation {
  const base = emptyAllocation();
  const bonus = skill.buffStatBonus;
  if (!bonus) return base;
  for (const id of STAT_IDS) {
    const n = bonus[id];
    if (typeof n === "number" && Number.isFinite(n) && n > 0) {
      base[id] = Math.floor(n);
    }
  }
  return base;
}

export function totalBuffApSpent(allocation: StatAllocation): number {
  return STAT_IDS.reduce((sum, id) => sum + allocation[id], 0);
}

export function skillBuffApRemaining(skill: SkillDefinition, allocation: StatAllocation): number {
  return skillBuffApRemainingForCards(skill.skillApCards, allocation);
}

export function skillBuffApRemainingForCards(
  skillApCards: number | undefined,
  allocation: StatAllocation,
): number {
  return Math.max(0, skillBuffApBudgetForCards(skillApCards) - totalBuffApSpent(allocation));
}

export function buffStatBonusRecord(
  allocation: StatAllocation,
): Partial<Record<StatId, number>> | undefined {
  const out: Partial<Record<StatId, number>> = {};
  for (const id of STAT_IDS) {
    const n = allocation[id];
    if (n > 0) out[id] = n;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function allocateBuffStatPoint(
  skill: SkillDefinition,
  allocation: StatAllocation,
  statId: StatId,
): StatAllocation | null {
  return allocateBuffStatPointForCards(skill.skillApCards, allocation, statId);
}

export function allocateBuffStatPointForCards(
  skillApCards: number | undefined,
  allocation: StatAllocation,
  statId: StatId,
): StatAllocation | null {
  if (skillBuffApRemainingForCards(skillApCards, allocation) <= 0) return null;
  return { ...allocation, [statId]: allocation[statId] + 1 };
}

export function deallocateBuffStatPointForCards(
  allocation: StatAllocation,
  statId: StatId,
): StatAllocation | null {
  if (allocation[statId] <= 0) return null;
  return { ...allocation, [statId]: allocation[statId] - 1 };
}

export type UpgradeSkillApCardResult =
  | { ok: true; economy: HomeEconomy; skill: SkillDefinition }
  | { ok: false; reason: string };

export function upgradeSkillWithApCard(
  economy: HomeEconomy,
  skill: SkillDefinition,
): UpgradeSkillApCardResult {
  if (skill.kind !== "buff") {
    return { ok: false, reason: "ใช้ได้กับสกิลบัฟเท่านั้น" };
  }
  const applied = skillApCardsApplied(skill);
  if (economy.skillApCards < 1) {
    return { ok: false, reason: "ไม่มีบัตร AP ในคลัง · ซื้อที่ร้าน Cash" };
  }
  return {
    ok: true,
    economy: {
      ...economy,
      skillApCards: economy.skillApCards - 1,
    },
    skill: {
      ...skill,
      skillApCards: applied + 1,
    },
  };
}
